import { useCallback, useEffect, useMemo, useState } from 'react'

export interface SeriesHighlightApi {
  // Picked ids, already filtered down to the ids currently on the chart.
  highlightedIds: string[]
  toggle: (id: string) => void
  clear: () => void
  // Whether a series should be drawn: everything when nothing is picked,
  // otherwise only the picks.
  isPlotted: (id: string) => boolean
}

// Whether the picks leave nothing out of the plotted set, i.e. isolate nothing.
// One shared definition so `toggle` and the derived state cannot drift apart.
export function coversAll(picks: string[], available: Set<string>): boolean {
  if (available.size === 0) return false
  const picked = new Set(picks)
  return Array.from(available).every((id) => picked.has(id))
}

// Picks that are still plotted, collapsed to empty when they cover everything.
function effectivePicks(picks: string[], available: Set<string>): string[] {
  const stillPlotted = picks.filter((id) => available.has(id))
  return coversAll(stillPlotted, available) ? [] : stillPlotted
}

/**
 * Isolate-on-click state for the trends charts' custom legend.
 *
 * Modelled as a set of *picked* (kept) ids rather than hidden ones so the empty
 * state means "show everything" — that way a filter change that swaps the chart's
 * series can never leave the canvas blank.
 *
 * `availableIds` exists for exactly that reason: picks that are no longer plotted
 * are dropped from the effective set instead of silently isolating nothing.
 *
 * Picking every available series isolates nothing, so it is the same state as
 * picking none: the picks collapse back to empty. Otherwise "Show all" would
 * linger on a chart that is already showing everything. The stored picks are
 * pruned the same way whenever `availableIds` changes, so picks that dropped out
 * (or collapsed) cannot come back and re-isolate if the series return later.
 */
export function useSeriesHighlight(availableIds: string[]): SeriesHighlightApi {
  const [pickedIds, setPickedIds] = useState<string[]>([])

  const available = useMemo(() => new Set(availableIds), [availableIds])

  const highlightedIds = useMemo(() => effectivePicks(pickedIds, available), [pickedIds, available])

  // An empty plotted set is a refetch gap, not a verdict on the picks: the same
  // series come back with the data, so the picks are kept until then. The derived
  // `highlightedIds` is already empty in that gap, which keeps the canvas unfiltered.
  useEffect(() => {
    if (available.size === 0) return
    setPickedIds((prev) => {
      const pruned = effectivePicks(prev, available)
      return pruned.length === prev.length ? prev : pruned
    })
  }, [available])

  const highlighted = useMemo(() => new Set(highlightedIds), [highlightedIds])

  const toggle = useCallback(
    (id: string) => {
      setPickedIds((prev) => effectivePicks(prev.includes(id) ? prev.filter((pickedId) => pickedId !== id) : [...prev, id], available))
    },
    [available]
  )

  const clear = useCallback(() => setPickedIds([]), [])

  const isPlotted = useCallback((id: string) => highlighted.size === 0 || highlighted.has(id), [highlighted])

  return { highlightedIds, toggle, clear, isPlotted }
}
